import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import { IconComponent } from '../icon/icon.component';

/**
 * The list-header filter control: an icon-only button that opens the filter
 * dialog, carrying a small clear badge on its top-right corner once filters are
 * applied.
 *
 * The badge replaces the separate clear-filters button that used to sit beside
 * it. Two adjacent icon-only squares read as two unrelated actions, and the
 * second one appeared and disappeared as filters came and went, shifting every
 * action to its right. Hanging the affordance off the control it acts on keeps
 * the row's action count stable and ties "clear" to "filter" visually.
 *
 * The badge is a real sibling `<button>`, not a nested one — a button inside a
 * button is invalid markup and browsers do not deliver the inner click — so the
 * host is a plain positioned wrapper.
 *
 *   <ui-filter-action
 *     [active]="hasModuleFilters()"
 *     (filter)="setFilter()"
 *     (clear)="clearFilter()">
 *   </ui-filter-action>
 */
@Component({
  selector: 'ui-filter-action',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, TranslateModule, IconComponent],
  templateUrl: './filter-action.component.html',
  styleUrls: ['./filter-action.component.scss'],
})
export class FilterActionComponent {
  /**
   * Whether any filter is currently applied. Drives both the button's active
   * treatment and whether the clear badge is shown at all.
   */
  @Input() active = false;

  /** Opens the filter dialog. */
  @Output() filter = new EventEmitter<void>();

  /** Clears every applied filter. */
  @Output() clear = new EventEmitter<void>();

  /**
   * The badge sits on top of the filter button, so a click there would otherwise
   * also read as "open the filter dialog" on its way up the DOM.
   */
  onClear(event: MouseEvent): void {
    event.stopPropagation();
    this.clear.emit();
  }
}
